import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronDown, ChevronRight, Lock, ShieldAlert, Users } from 'lucide-react';
import { wissenApi, type Wissensbezugsart, type Wissenssichtbarkeit } from '../services/api';
import { WissensInhalt } from './WissensInhalt';
import { useAuth } from '../context/AuthContext';

/**
 * Die Hinweise aus der Wissensdatenbank — dort, wo sie gebraucht werden.
 *
 * ## Warum das eine eigene Komponente ist
 *
 * Ein Wissensbestand, den man nur über eine eigene Seite erreicht, wird
 * während der Begehung nicht gelesen. Er wird gelesen, wenn jemand vor
 * dem Bauteil steht und nicht weiß, wie ein Wandanschluss auszusehen
 * hat — also am Prüfpunkt und am Mangel.
 *
 * Deshalb hängt die Komponente an einem Bezug (`art` + `ziel`) und nicht
 * an einer Artikelkennung: die aufrufende Seite weiß, WORÜBER gerade
 * gearbeitet wird, aber nicht, welcher Artikel dazu geschrieben wurde.
 *
 * ## Warum sie sich still zurückzieht
 *
 * Gibt es keinen Artikel zu diesem Bezug, erscheint nichts. Ein
 * dauerhaft leerer Kasten „keine Hinweise vorhanden" auf jedem
 * Mangelformular wäre Rauschen, das nach zwei Tagen niemand mehr liest —
 * und dann fällt auch der Fall nicht mehr auf, in dem etwas darin steht.
 */

const STUFEN_ANZEIGE: Record<
  Wissenssichtbarkeit,
  { text: string; klasse: string; symbol: typeof Lock }
> = {
  KUNDE: {
    text: 'Kundensichtbar',
    klasse: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    symbol: Users,
  },
  INTERN: {
    text: 'Intern',
    klasse: 'bg-amber-50 text-amber-700 border-amber-200',
    symbol: Lock,
  },
  VERTRAULICH: {
    text: 'Vertraulich',
    klasse: 'bg-red-50 text-red-700 border-red-200',
    symbol: ShieldAlert,
  },
};

export function Sichtbarkeitsmarke({ stufe }: { stufe: Wissenssichtbarkeit }) {
  const anzeige = STUFEN_ANZEIGE[stufe] ?? STUFEN_ANZEIGE.INTERN;
  const Symbol = anzeige.symbol;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold ${anzeige.klasse}`}
    >
      <Symbol size={11} /> {anzeige.text}
    </span>
  );
}

interface Props {
  art: Wissensbezugsart;
  /** Prüfbereichsschlüssel, Prüfpunktkennung oder Mangelkategorie. */
  ziel: string | null | undefined;
  /** Überschrift des Kastens. */
  titel?: string;
}

export function WissensPanel({ art, ziel, titel = 'Hinweise' }: Props) {
  const { hatRecht } = useAuth();
  const [offen, setOffen] = useState<number | null>(null);

  const darfLesen = hatRecht('wissen.lesen');

  const { data } = useQuery({
    queryKey: ['wissen', 'kontext', art, ziel],
    queryFn: () => wissenApi.kontext(art, ziel as string),
    // Ohne Ziel gibt es nichts nachzuschlagen, und ohne Recht gäbe der
    // Server ohnehin 403 — die Abfrage bliebe eine Fehlermeldung in der
    // Konsole, die niemandem hilft.
    enabled: Boolean(ziel) && darfLesen,
    staleTime: 5 * 60 * 1000,
  });

  if (!data || data.length === 0) return null;

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-bold text-sky-900">
        <BookOpen size={15} />
        {titel}
        <span className="font-normal text-sky-700">({data.length})</span>
      </div>

      <ul className="space-y-1.5">
        {data.map((artikel) => {
          const istOffen = offen === artikel.id;
          return (
            <li key={artikel.id} className="rounded-lg border border-sky-200 bg-white">
              <button
                type="button"
                onClick={() => setOffen(istOffen ? null : artikel.id)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left"
              >
                {istOffen ? (
                  <ChevronDown size={15} className="mt-0.5 shrink-0 text-sky-600" />
                ) : (
                  <ChevronRight size={15} className="mt-0.5 shrink-0 text-sky-600" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-secondary-900">
                      {artikel.titel}
                    </span>
                    <Sichtbarkeitsmarke stufe={artikel.sichtbarkeit} />
                    <span className="text-[11px] text-secondary-400">v{artikel.version}</span>
                  </span>
                  {artikel.kurzfassung && (
                    <span className="mt-0.5 block text-xs text-secondary-600">
                      {artikel.kurzfassung}
                    </span>
                  )}
                </span>
              </button>

              {istOffen && (
                <div className="border-t border-sky-100 px-3 py-2">
                  {/* Die Fundstelle steht VOR dem Text: sie ist der Grund,
                      warum jemand den Hinweis akzeptiert. Der Normwortlaut
                      selbst steht bewusst nirgends — er ist geschützt. */}
                  {(artikel.norm_ausgabe || artikel.norm_abschnitt) && (
                    <p className="mb-2 text-xs text-secondary-500">
                      Fundstelle:{' '}
                      {[artikel.norm_ausgabe, artikel.norm_abschnitt]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                  <WissensInhalt html={artikel.inhalt_html} />
                  <Link
                    to={`/wissen?artikel=${artikel.id}`}
                    className="mt-2 inline-block text-xs font-medium text-primary-600 hover:underline"
                  >
                    In der Wissensdatenbank öffnen
                  </Link>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
